/**
 * RegistrationTabs — the Registration step's bottom tab control, faithful to
 * the legacy XAF detail view and the approved prototype:
 *
 *   Received Dates · Attachments · Sample Condition Check · Custom Request ·
 *   Notes · Item Charge · Image Edit
 *
 * Each tab is a shared <AppDataGrid> plus its own New / Edit / Delete popup
 * (the shared <AppDialog>).  Grid styling, per-column filters, pagination and
 * the footer all come from AppDataGrid — nothing is hand-rolled here.
 *
 * A Job ID can receive samples MULTIPLE times: Received Dates is the list of
 * receipt events, and both Attachments and Sample Condition Checks link to one
 * receipt through its Received Date.
 *
 * Mutations update local state; the parent's `persist()` atomically saves all
 * child collections (samples, tests, bottles, notes, SCC, item charges,
 * attachments, images) in a single API call.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar, ExportMenu } from '../../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { downloadBlob } from '../../../utils/exportData';
import { LookupCombo } from './LookupCombo';
import { inertProps } from './inertProps';
import { useConfirm } from '../../../components/confirm';
import { api } from '../../../api/client';
import {
  ATT_CAT_LABELS, ATT_CAT_OPTIONS, ATT_CAT_VALUES,
  SCC_POINTS, nextKey, resolveDueDate, toDateInput, toDateTimeInput,
  type IcPricingOption, type SrAttachment, type SrHeader, type SrImage, type SrItemCharge, type SrNote,
  type SrLookup, type SrReceivedDate, type SrSccComment, type SrSccRecord, type SrTabState, type SrTest,
} from './srData';

const TAB_LABELS = [
  'Received Dates', 'Attachments', 'Sample Condition Check', 'Custom Request',
  'Notes', 'Item Charge', 'Image Edit', 'Pre-Invoice',
] as const;

// Pre-Invoice is hidden until the owner confirms its flow (2026-10-08). Remove the
// entry to bring the tab back — its body component is kept intact.
const HIDDEN_TABS: ReadonlySet<string> = new Set(['Pre-Invoice']);

type TabIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface RegistrationTabsProps {
  header: SrHeader;
  jobId: string;
  /** The current user (Created By / Operator / Author). */
  user: string;
  /** Server-local "now" the form stamps new child records with. */
  now: string;
  tabs: SrTabState;
  setTabs: (next: SrTabState) => void;
  /** Header-level test template — the Custom Request tab's rows. */
  templateTests: SrTest[];
  /** Per-test due-date override + hold + sub-out + TAT, keyed by test name. */
  customRequest: Record<string, { hold: boolean; subOut: boolean; due: string; tat: string }>;
  setCustomRequest: (next: Record<string, { hold: boolean; subOut: boolean; due: string; tat: string }>) => void;
  /** TAT name → hours map (from rule data). Used for per-test DueDate recalc. */
  tatHoursMap: Record<string, number>;
  /** Holiday date strings (YYYY-MM-DD) — used by resolveDueDate. */
  holidaySet: ReadonlySet<string>;
  /** 'Sampling' | 'Environmental' etc. — passed to resolveDueDate. */
  registrationType: string;
  /** Lookup catalogs for dropdown fields in child dialogs. */
  lookups: Record<string, SrLookup>;
  /** Server Oid (Guid) of the saved job — undefined/null for an unsaved New record. */
  jobOid?: string | null;
  onWarn: (message: string) => void;
  onInfo: (message: string) => void;
  onSuccess: (message: string) => void;
  /** Shared confirm for every state-changing tab action (standing rule 7). */
  confirmDelete: (what: string, run: () => void) => void;
  /**
   * Persist the whole job (the parent `persist()`), returning the resolved JobID
   * (or null on failure). Used by the SCC Report to save the SCC to the DB BEFORE
   * previewing — GoLive parity (SampleConditionCheckViewController.SCC_Report_Execute
   * commits the SampleConditionCheck via `View.ObjectSpace.CommitChanges()` first,
   * then renders SCC_Report_CheckPoints_View, which reads from the DB).
   */
  onSaveForReport?: () => Promise<string | null>;
  /** Save the (new) registration so it has a JobID, returning it (null on failure/validation). */
  onEnsureJob?: () => Promise<string | null>;
  /** MAC read-only mode: the tab bodies are inert (tabs can still be switched to view). */
  readOnly?: boolean;
}

export function RegistrationTabs(props: RegistrationTabsProps) {
  const [tab, setTab] = useState<TabIndex>(0);

  return (
    <section className="sr-sec">
      <div className="sr-sec-b" style={{ paddingBottom: 6 }}>
        <div className="sr-tabbar" role="tablist">
          {TAB_LABELS.map((label, i) => HIDDEN_TABS.has(label) ? null : (
            <button
              key={label}
              role="tab"
              aria-selected={tab === i}
              className={`sr-tb${tab === i ? ' active' : ''}`}
              onClick={() => setTab(i as TabIndex)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ minHeight: 90 }} {...inertProps(!!props.readOnly)}>
          {tab === 0 && <ReceivedDatesTab {...props} />}
          {tab === 1 && <AttachmentsTab {...props} />}
          {tab === 2 && <SccTab {...props} />}
          {tab === 3 && <CustomRequestTab {...props} />}
          {tab === 4 && <NotesTab {...props} />}
          {tab === 5 && <ItemChargeTab {...props} />}
          {tab === 6 && <ImageEditTab {...props} />}
          {tab === 7 && <PreInvoiceTab {...props} />}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Received Dates — one row per receipt event
// ─────────────────────────────────────────────────────────────────────────────
function ReceivedDatesTab({
  header, user, now, tabs, setTabs, lookups, onWarn, confirmDelete,
}: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrReceivedDate | null>(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const blank = (): SrReceivedDate => ({
    key: nextKey('rd'), oid: '', date: now, by: user, relinq: header.relinquishedBy.join('; '), n: 1,
    matrix: [...header.matrices], tat: header.tat, cooler: header.coolerId,
    cat: [...header.sampleCategory], createdBy: user, created: now,
  });

  const save = (rec: SrReceivedDate) => {
    if (!rec.date) { onWarn('Enter the Received Date and time for this receipt.'); return; }
    const exists = tabs.receivedDates.some(r => r.key === rec.key);
    setTabs({
      ...tabs,
      receivedDates: exists
        ? tabs.receivedDates.map(r => (r.key === rec.key ? rec : r))
        : [...tabs.receivedDates, rec],
    });
    setOpen(false);
  };

  const cols: AppColumn<SrReceivedDate>[] = [
    editCol<SrReceivedDate>(r => { setEditing(r); setOpen(true); }),
    { field: 'date', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.date) },
    { field: 'by', header: 'ReceivedBy', width: 110 },
    { field: 'relinq', header: 'RelinquishedBy', width: 130 },
    { field: 'n', header: 'NoOfSamples', type: 'number', width: 105 },
    { field: 'matrix', header: 'SampleMatrix', width: 130, text: r => r.matrix.join('; ') },
    { field: 'tat', header: 'TAT', width: 90 },
    { field: 'cooler', header: 'CoolerID', width: 100 },
    { field: 'cat', header: 'SampleCategory', width: 130, text: r => r.cat.join('; ') },
    { field: 'createdBy', header: 'CreatedBy', width: 105 },
    { field: 'created', header: 'CreatedDate', width: 120, text: r => fmtLocalDate(r.created) },
  ];

  return (
    <>
      <AppToolbar
        onNew={() => { setEditing(blank()); setOpen(true); }}
        onEdit={() => {
          const rec = tabs.receivedDates.find(r => sel.has(r.key));
          if (rec) { setEditing({ ...rec }); setOpen(true); }
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} received date(s)`, () => {
          setTabs({ ...tabs, receivedDates: tabs.receivedDates.filter(r => !sel.has(r.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
        exportData={{ filename: 'ReceivedDates', ...gridExportMatrix(tabs.receivedDates, cols) }}
      />
      <AppDataGrid<SrReceivedDate>
        rows={tabs.receivedDates}
        columns={cols}
        getRowId={r => r.key}
        selectable
        selectedIds={sel}
        onSelectionChange={setSel}
        defaultPageSize={10}
        emptyText="No data to display"
      />

      {open && editing && (
        <ReceivedDateDialog
          record={editing}
          lookups={lookups}
          onChange={setEditing}
          onClose={() => setOpen(false)}
          onSave={save}
        />
      )}
    </>
  );
}

function ReceivedDateDialog({ record, lookups, onChange, onClose, onSave }: {
  record: SrReceivedDate;
  lookups: Record<string, SrLookup>;
  onChange: (r: SrReceivedDate) => void; onClose: () => void; onSave: (r: SrReceivedDate) => void;
}) {
  const set = (p: Partial<SrReceivedDate>) => onChange({ ...record, ...p });
  const employeeLk = lookups.employee ?? { cols: ['Name'], rows: [] };
  const relinqLk = lookups.relinq ?? { cols: ['Name'], rows: [] };
  const tatLk = lookups.tat ?? { cols: ['TAT'], rows: [] };
  const matrixLk = lookups.matrix ?? { cols: ['Matrix'], rows: [] };
  const categoryLk = lookups.sampleCategory ?? { cols: ['Sample Category'], rows: [] };
  return (
    <AppDialog open title={`${fmtLocalDateTime(record.date)} – ReceivedDate`} icon="ti-calendar-event" width={820} className="sr-dlg" onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => onSave(record)}>OK</button>
      </>}>
      <div className="sr-fgrid">
        <label className="req">Received Date:</label>
        <input type="datetime-local" value={toDateTimeInput(record.date)}
          className={record.date ? undefined : 'sr-req-empty'}
          onChange={e => set({ date: e.target.value })} />
        <label>Relinquished By:</label>
        <LookupCombo lookup={relinqLk} value={record.relinq}
          onPick={r => set({ relinq: r[0] })} popupMatchWidth listMaxHeight={140} />

        <label>Received By:</label>
        <LookupCombo lookup={employeeLk} value={record.by}
          onPick={r => set({ by: r[0] })} popupMatchWidth listMaxHeight={140} />
        <label>TAT:</label>
        <LookupCombo lookup={tatLk} value={record.tat}
          onPick={r => set({ tat: r[0] })} popupMatchWidth listMaxHeight={140} />

        <label>No Of Samples:</label>
        <span className="sr-numspin">
          <button type="button" onClick={() => set({ n: Math.max(0, record.n - 1) })}>−</button>
          <input value={record.n} readOnly aria-label="No Of Samples" />
          <button type="button" onClick={() => set({ n: record.n + 1 })}>＋</button>
        </span>
        <label>Cooler Id:</label>
        <input value={record.cooler} onChange={e => set({ cooler: e.target.value })} />

        <label>Sample Matrix:</label>
        <LookupCombo lookup={matrixLk} multi values={record.matrix}
          onToggle={next => set({ matrix: next })} popupMatchWidth listMaxHeight={140} />
        <label>Created By:</label>
        <input value={record.createdBy} readOnly />

        <label>Sample Category:</label>
        <LookupCombo lookup={categoryLk} multi values={record.cat}
          onToggle={next => set({ cat: next })} popupMatchWidth listMaxHeight={140} />
        <label>Created Date:</label>
        <input value={fmtLocalDate(record.created)} readOnly />
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Attachments
// ─────────────────────────────────────────────────────────────────────────────
function AttachmentsTab({
  header, user, now, jobOid, tabs, setTabs, onWarn, onSuccess, confirmDelete,
}: RegistrationTabsProps) {
  const [open, setOpen] = useState(false);
  const [cocOpen, setCocOpen] = useState(false);
  const [cocTemplate, setCocTemplate] = useState('');
  const [cocBusy, setCocBusy] = useState(false);
  const [editing, setEditing] = useState<SrAttachment | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const catLabel = (cat: string) => {
    const n = Number(cat);
    return Number.isFinite(n) ? (ATT_CAT_LABELS[n] ?? cat) : cat;
  };

  const download = async (r: SrAttachment) => {
    if (!r.fileDataOid) { onWarn('No file attached to this record.'); return; }
    try {
      const res = await api.get(
        `/api/v1/sample-registration/attachments/${r.fileDataOid}/download`,
        { responseType: 'blob' },
      );
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.file || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onWarn('Failed to download the attachment.');
    }
  };

  const cols: AppColumn<SrAttachment>[] = [
    editCol<SrAttachment>(r => { setEditing({ ...r }); setOpen(true); }),
    { field: 'name', header: 'Name', width: 170 },
    { field: 'cat', header: 'Category', width: 110, text: r => catLabel(r.cat) },
    {
      field: 'file', header: 'Attachment', width: 220,
      render: r => r.fileDataOid
        ? <button className="sr-link-btn" onClick={() => download(r)} title="Download">📎 {r.file || 'Download'}</button>
        : <span>📎 {r.file || '—'}</span>,
    },
    { field: 'date', header: 'Date', width: 110, text: r => fmtLocalDate(r.date) },
    { field: 'op', header: 'Operator', width: 110 },
    { field: 'recv', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.recv) },
  ];

  const attachCoc = async () => {
    if (!jobOid) { onWarn('Save the job first before attaching a COC document.'); return; }
    if (!cocTemplate.trim()) { onWarn('Enter a COC report template name.'); return; }
    setCocBusy(true);
    try {
      const res = await api.post(
        `/api/v1/sample-registration/${jobOid}/generate-coc-attachment`,
        null,
        { params: { templateName: cocTemplate.trim() } },
      );
      if (res.data?.success) {
        const d = res.data.data as { attachmentOid: string; fileDataOid: string; fileName: string };
        const newRow: SrAttachment = {
          key: nextKey('att'), oid: d.attachmentOid, name: d.fileName,
          cat: String(ATT_CAT_VALUES.COC), file: d.fileName,
          fileDataOid: d.fileDataOid, date: now, op: user, opOid: '', recv: '', comment: '',
        };
        setTabs({ ...tabs, attachments: [...tabs.attachments, newRow] });
        setCocOpen(false);
        setCocTemplate('');
        onSuccess('COC document generated and attached successfully.');
      } else {
        onWarn(res.data?.error?.message ?? 'Failed to generate COC attachment.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Failed to generate COC attachment.';
      onWarn(msg);
    } finally {
      setCocBusy(false);
    }
  };

  const openNew = () => {
    setEditing({
      key: nextKey('att'), oid: '', name: '', cat: String(ATT_CAT_VALUES.COC),
      file: '', fileDataOid: '',
      // ALPACA parity: Received Date defaults to the registration's own Received Date.
      date: now, op: user, opOid: '',
      recv: tabs.receivedDates[0]?.date ?? (header.receivedDate || ''), comment: '',
    });
    if (fileRef.current) fileRef.current.value = '';
    setOpen(true);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editing) return;
    const f = e.target.files?.[0];
    if (!f) return;
    // ALPACA parity (user report 2026-10-03): selecting a file auto-fills the
    // Name with the file's name SANS extension (e.g. "00.1-after-login-dashboard.png"
    // → "00.1-after-login-dashboard"). The user can still edit it before saving.
    const baseName = f.name.replace(/\.[^./\\]+$/, '');
    setEditing({ ...editing, file: f.name, fileObj: f, name: baseName });
  };

  const uploadFile = async (file: File): Promise<{ fileDataOid: string; fileName: string } | null> => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/v1/sample-registration/attachments/upload', fd);
      if (res.data?.success && res.data.data) {
        return { fileDataOid: res.data.data.fileDataOid, fileName: res.data.data.fileName };
      }
      return null;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      if (msg) onWarn(msg);
      return null;
    }
  };

  const save = async () => {
    if (!editing) return;
    const isNew = !editing.oid;
    if (isNew && !editing.file && !editing.fileObj) {
      onWarn('Browse and select the Attachment file.'); return;
    }
    if (!editing.name.trim()) { onWarn('Enter the Name.'); return; }

    let rec = { ...editing };

    if (rec.fileObj) {
      setUploading(true);
      const result = await uploadFile(rec.fileObj);
      setUploading(false);
      if (!result) { onWarn('File upload failed. Please try again.'); return; }
      rec = { ...rec, fileDataOid: result.fileDataOid, file: result.fileName, fileObj: undefined };
    }

    const exists = tabs.attachments.some(a => a.key === rec.key);
    setTabs({
      ...tabs,
      attachments: exists
        ? tabs.attachments.map(a => (a.key === rec.key ? rec : a))
        : [...tabs.attachments, rec],
    });
    setOpen(false);
  };

  // Shared hotkey (standing rule 14, §18.6, ADR-0013): the bespoke Attach COC
  // toolbar button.  This tab mounts only when active, and the button always
  // renders here, so the def is unconditionally present whenever the button is.
  useHotkeys([
    { combo: 'alt+h', label: 'Attach COC', group: 'Sample Registration',
      run: () => { if (!jobOid) { onWarn('Save the job first before attaching a COC document.'); return; } setCocOpen(true); } },
  ]);

  return (
    <>
      <AppToolbar
        onNew={openNew}
        onEdit={() => {
          const rec = tabs.attachments.find(a => sel.has(a.key));
          if (rec) { setEditing({ ...rec }); setOpen(true); }
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} attachment(s)`, () => {
          setTabs({ ...tabs, attachments: tabs.attachments.filter(a => !sel.has(a.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
        exportData={{ filename: 'Attachments', ...gridExportMatrix(tabs.attachments, cols) }}
        left={
          <button
            className="ca-btn"
            title={`Generate a COC report PDF and attach it to this job (${formatCombo('alt+h')})`}
            onClick={() => { if (!jobOid) { onWarn('Save the job first before attaching a COC document.'); return; } setCocOpen(true); }}
          >
            <i className="ti ti-file-certificate" /> Attach COC
          </button>
        }
      />
      <AppDataGrid<SrAttachment>
        rows={tabs.attachments} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {cocOpen && (
        <AppDialog open title="Attach COC Document" icon="ti-file-certificate" width={420} className="sr-dlg"
          onClose={() => { setCocOpen(false); setCocTemplate(''); }}
          footer={<>
            <button className="ca-btn" onClick={() => { setCocOpen(false); setCocTemplate(''); }}>Cancel</button>
            <button className="ca-btn ca-btn-solid" disabled={cocBusy || !cocTemplate.trim()} onClick={attachCoc}>
              {cocBusy ? '⏳ Generating…' : '📎 Attach'}
            </button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
            <label className="req">Report Template</label>
            <input
              autoFocus
              placeholder="e.g. COC Report"
              value={cocTemplate}
              className={cocTemplate.trim() ? undefined : 'sr-req-empty'}
              onChange={e => setCocTemplate(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && cocTemplate.trim() && !cocBusy) attachCoc(); }}
            />
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--ai-muted)' }}>
            Enter the exact name of the COC report template configured in Custom Reporting.
            The rendered PDF will be stored as a COC attachment on this job.
          </p>
        </AppDialog>
      )}

      {open && editing && (
        <AppDialog open title="Attachment" icon="ti-paperclip" width={520} className="sr-dlg" onClose={() => setOpen(false)}
          footer={<>
            <button className="ca-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" disabled={uploading} onClick={save}>
              {uploading ? '⏳ Uploading…' : '💾 OK'}
            </button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
            <label className="req">Attachment</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {/* Bug 9: the native input's own "No file chosen" text ignores a file
                  already attached (edit) — hide it and show the file name ourselves,
                  as ALPACA shows the attachment's file name in the field. */}
              <input ref={fileRef} type="file" onChange={handleFilePick} style={{ display: 'none' }} />
              <button type="button" className="ca-btn" onClick={() => fileRef.current?.click()}>Choose file</button>
              <span title={editing.file || undefined}
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {editing.file || 'No file chosen'}
              </span>
            </div>
            <label className="req">Name</label>
            <input value={editing.name} className={editing.name ? undefined : 'sr-req-empty'}
              onChange={e => setEditing({ ...editing, name: e.target.value })} />
            <label className="req">Category</label>
            <select value={catLabel(editing.cat)}
              onChange={e => setEditing({ ...editing, cat: String(ATT_CAT_VALUES[e.target.value] ?? 5) })}>
              {ATT_CAT_OPTIONS.map(c => <option key={c}>{c}</option>)}
            </select>
            <label>Received Date</label>
            <select value={editing.recv} onChange={e => setEditing({ ...editing, recv: e.target.value })}>
              <option value="">N/A</option>
              {editing.recv && !tabs.receivedDates.some(r => r.date === editing.recv) && (
                <option value={editing.recv}>{fmtLocalDateTime(editing.recv)}</option>
              )}
              {tabs.receivedDates.map(r => (
                <option key={r.key} value={r.date}>{fmtLocalDateTime(r.date)}</option>
              ))}
            </select>
            <label>Date</label>
            <input value={fmtLocalDate(editing.date)} readOnly />
            <label>Comment</label>
            <input value={editing.comment} onChange={e => setEditing({ ...editing, comment: e.target.value })} />
            <label>Operator</label>
            <input value={editing.op} readOnly />
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Condition Check (SCC) — one per receipt, 17 check points + comments
// ─────────────────────────────────────────────────────────────────────────────
// pickAnswer int → answer string (0=Yes, 1=No, 2=NA)
const PICK_ANSWER: Record<number, string> = { 0: 'yes', 1: 'no', 2: 'na' };

function SccTab({
  header, jobId, user, now, tabs, setTabs, onWarn, confirmDelete, onSaveForReport, onEnsureJob,
}: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrSccRecord | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  // Live check-points from the DB catalog; falls back to the local constant
  // until the API responds (or if it fails, the fallback stays).
  const [sccPoints, setSccPoints] = useState<ReadonlyArray<readonly [string, string]>>(SCC_POINTS);

  const matricesKey = header.matrices.join(';');
  useEffect(() => {
    api.get<{ success: boolean; data: Array<{ checkPoint: string; pickAnswer: number; oid: string; sort: number }> }>(
      '/api/v1/sample-registration/scc-checkpoints',
      { params: matricesKey ? { matrices: matricesKey } : {} },
    ).then(res => {
      if (res.data?.data?.length) {
        const pts = [...res.data.data]
          .sort((a, b) => a.sort - b.sort)
          .map(r => [r.checkPoint, PICK_ANSWER[r.pickAnswer] ?? 'na'] as [string, string]);
        setSccPoints(pts);
      }
    }).catch(() => { /* keep SCC_POINTS fallback */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matricesKey]);

  const blank = (): SrSccRecord => ({
    key: nextKey('scc'), oid: '', matrix: header.matrices[0] ?? 'Water', by: user, date: now,
    // ALPACA parity: default to the registration's own Received Date (no SC_ReceivedDates row yet).
    recv: tabs.receivedDates[0]?.date ?? (header.receivedDate || now),
    temp: '', corr: '', thermo: '', ph: '', paper: '',
    points: sccPoints.map(p => p[1]),
    descriptions: sccPoints.map(() => ''),
    comments: [],
  });

  // Preview SCC report via backend PDF engine (SCC_CheckPoints_Report template,
  // DR: SCC_Report_CheckPoints_View filtered by JobID). Mirrors XAF SCC_Report_Execute.
  //
  // GoLive parity — SAVE-THEN-REPORT. SampleConditionCheckViewController.SCC_Report_Execute
  // calls `View.ObjectSpace.CommitChanges()` (persist the SCC) BEFORE it renders the
  // report, because SCC_Report_CheckPoints_View reads the SampleConditionCheck straight
  // from the DB. In AI the SCC lives in this tab's local state until the whole job is
  // saved, so an SCC just entered in the grid has no DB row yet and the report came
  // back empty ("produced no content"). We therefore persist the job first (exactly
  // like GoLive), then preview with the resolved JobID.
  const previewSccReport = async () => {
    let reportJobId = jobId;

    if (tabs.sccRecords.length === 0) {
      onWarn('Add a Sample Condition Check before previewing the SCC report.');
      return;
    }

    // GoLive commits ONLY the SCC before reporting (View.ObjectSpace.CommitChanges);
    // AI's persist() saves the WHOLE job, which can fail on an in-progress job
    // (validation / a slow save that times out). So save first ONLY when the SCC
    // has UNSAVED local edits (a New row with no server Oid yet); a SCC already in
    // the DB is reported directly — instant, and a job that can't fully save never
    // blocks the report. The save is best-effort: on failure we still report the
    // last-committed SCC from the DB rather than dead-ending.
    const hasUnsavedScc = tabs.sccRecords.some(r => !r.oid);
    if (hasUnsavedScc && onSaveForReport) {
      const savedJobId = await onSaveForReport();
      if (savedJobId) reportJobId = savedJobId;
      // on failure: fall through and report whatever is already saved
    }

    if (!reportJobId) {
      onWarn('Please save the registration before previewing the SCC report.');
      return;
    }

    // GoLive scopes the SCC report to the SELECTED SampleConditionCheck (SCCOid),
    // not the whole job — a job can hold more than one SCC (multiple check-in
    // events), and reporting by JobID prints every check-point once per SCC
    // (duplicate rows). Report the checked row(s); when nothing is checked but a
    // single saved SCC exists, use it; if several exist and none is checked, ask
    // the user to pick one. A brand-new (unsaved) SCC has no Oid yet → fall back
    // to the JobID scope (a single-SCC job never duplicates).
    const savedRecs = tabs.sccRecords.filter(r => r.oid);
    const selectedOids = savedRecs.filter(r => sel.has(r.key)).map(r => r.oid);
    let sccOids: string[] = [];
    if (selectedOids.length > 0) sccOids = selectedOids;
    else if (savedRecs.length === 1) sccOids = [savedRecs[0]!.oid];
    else if (savedRecs.length > 1) {
      onWarn('This job has more than one Sample Condition Check — select one row to preview its report.');
      return;
    }

    try {
      const res = await api.get('/api/v1/sample-registration/report-pdf', {
        params: sccOids.length > 0
          ? { templateName: 'SCC_CheckPoints_Report', sccOids: sccOids.join(',') }
          : { templateName: 'SCC_CheckPoints_Report', jobIds: reportJobId },
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        onWarn(`SCC Report produced no content for job ${reportJobId}. Ensure the SCC is saved.`);
        return;
      }
      // GoLive parity — DOWNLOAD the PDF (it does not open a preview tab). GoLive's
      // SCC_Report_Execute ends with `Frame.GetController<DownloadController>()
      // .Download_Execute(base64Pdf, timeStamp, "pdf")`, i.e. the browser downloads
      // the file named with a yyyyMMddHHmmss timestamp; the user then clicks the
      // downloaded PDF to preview it. We reproduce that exactly (shared downloadBlob,
      // §14b "don't reinvent").
      const t = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp =
        `${t.getFullYear()}${pad(t.getMonth() + 1)}${pad(t.getDate())}` +
        `${pad(t.getHours())}${pad(t.getMinutes())}${pad(t.getSeconds())}`;
      downloadBlob(blob, `${stamp}.pdf`);
    } catch {
      onWarn('Failed to preview the SCC Report. The report could not be generated.');
    }
  };

  // ALPACA parity: New on the nested SCC list of an UNSAVED registration makes XAF commit
  // the registration first (ObjectSpace_Committing mints the JobID), so the popup opens
  // with the real Job ID and the registration's Received Date. Do the same: persist the
  // job, then open the popup. A job never explicitly saved is discarded again on Cancel.
  const newScc = async () => {
    if (!jobId && onEnsureJob) {
      const id = await onEnsureJob();
      if (!id) return;   // validation / save failure already reported to the user
    }
    setEditing(blank());
  };

  const save = (rec: SrSccRecord) => {
    const exists = tabs.sccRecords.some(r => r.key === rec.key);
    setTabs({
      ...tabs,
      sccRecords: exists ? tabs.sccRecords.map(r => (r.key === rec.key ? rec : r)) : [...tabs.sccRecords, rec],
    });
    setEditing(null);
  };

  const cols: AppColumn<SrSccRecord>[] = [
    editCol<SrSccRecord>(r => setEditing({ ...r, points: [...r.points], comments: [...r.comments] })),
    { field: 'matrix', header: 'SampleMatrix', width: 140 },
    { field: 'by', header: 'CheckInBy', width: 120 },
    { field: 'date', header: 'CheckInDate', width: 120, text: r => fmtLocalDate(r.date) },
    { field: 'recv', header: 'ReceivedDate', width: 150, text: r => fmtLocalDateTime(r.recv) },
    { field: 'corr', header: 'CorrectedTemperature', width: 170 },
  ];

  // Shared hotkey (standing rule 14, §18.6, ADR-0013): the bespoke SCC Report
  // toolbar button (the dialog's own Report button is left untouched).  This tab
  // mounts only when active and the button always renders here.
  useHotkeys([
    { combo: 'alt+p', label: 'SCC Report', group: 'Sample Registration',
      run: () => void previewSccReport() },
  ]);

  return (
    <>
      <AppToolbar
        left={
          <button className="ca-btn"
            title={`SCC Report (${formatCombo('alt+p')})`}
            onClick={() => void previewSccReport()}>
            <span className="tb-i tb-i-blue">📄</span> SCC Report
          </button>
        }
        onNew={() => void newScc()}
        onEdit={() => {
          const rec = tabs.sccRecords.find(r => sel.has(r.key));
          if (rec) setEditing({ ...rec, points: [...rec.points], comments: [...rec.comments] });
        }}
        editDisabled={sel.size !== 1}
        onDelete={() => confirmDelete(`${sel.size} SCC record(s)`, () => {
          setTabs({ ...tabs, sccRecords: tabs.sccRecords.filter(r => !sel.has(r.key)) });
          setSel(new Set());
        })}
        deleteDisabled={sel.size === 0}
        exportData={{ filename: 'SampleConditionCheck', ...gridExportMatrix(tabs.sccRecords, cols) }}
      />
      <AppDataGrid<SrSccRecord>
        rows={tabs.sccRecords} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No SCC yet. Click New to record sample conditions."
      />

      {editing && (
        <SccDialog
          record={editing} header={header} jobId={jobId} user={user}
          receipts={tabs.receivedDates.length > 0
            ? tabs.receivedDates.map(r => r.date)
            : (header.receivedDate ? [header.receivedDate] : [])}
          checkPoints={sccPoints}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          onReport={() => void previewSccReport()}
        />
      )}
    </>
  );
}

function SccDialog({ record, header, jobId, user, receipts, checkPoints, onChange, onClose, onSave, onReport }: {
  record: SrSccRecord; header: SrHeader; jobId: string; user: string; receipts: string[];
  checkPoints: ReadonlyArray<readonly [string, string]>;
  onChange: (r: SrSccRecord) => void; onClose: () => void;
  onSave: (r: SrSccRecord) => void; onReport: () => void;
}) {
  const set = (p: Partial<SrSccRecord>) => onChange({ ...record, ...p });
  // The Comment "New" button opens a popup (ALPACA "Sample Condition Check Comment"
  // dialog) rather than using an inline box — the inline box's New did nothing when
  // the box was empty (user report 2026-10-06).
  const [showCommentDlg, setShowCommentDlg] = useState(false);
  // ALPACA parity: the Comment list has New · Edit · Delete (checkbox rows + a row edit
  // pencil). `editIdx` = the comment being edited in the popup (null = adding a new one).
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [commentSel, setCommentSel] = useState<Set<string | number>>(new Set());
  const { confirm: confirmComment } = useConfirm();

  const answer = (i: number, v: string) => {
    const points = [...record.points];
    points[i] = points[i] === v ? '' : v;   // Yes / No / N-A are mutually exclusive
    set({ points });
  };

  const addComment = (text: string) => {
    const t = text.trim();
    if (!t) return;
    if (editIdx !== null) {
      // Edit: change the text only — author and time stay as originally stamped.
      set({ comments: record.comments.map((c, i) => (i === editIdx ? { ...c, text: t } : c)) });
    } else {
      const c: SrSccComment = { text: t, user, dt: record.date };
      set({ comments: [...record.comments, c] });
    }
    setEditIdx(null);
  };
  const openNewComment = () => { setEditIdx(null); setShowCommentDlg(true); };
  const openEditComment = (i: number) => { setEditIdx(i); setShowCommentDlg(true); };
  const editSelectedComment = () => {
    const i = [...commentSel][0];
    if (commentSel.size === 1 && typeof i === 'number') openEditComment(i);
  };
  const deleteSelectedComments = async () => {
    if (commentSel.size === 0) return;
    const ok = await confirmComment({
      title: 'Delete', message: `Delete ${commentSel.size} comment(s)? This cannot be undone.`, tone: 'danger',
    });
    if (!ok) return;
    set({ comments: record.comments.filter((_, i) => !commentSel.has(i)) });
    setCommentSel(new Set());
  };
  const commentRows = record.comments.map((c, i) => ({ ...c, idx: i }));
  const commentCols: AppColumn<SrSccComment & { idx: number }>[] = [
    editCol<SrSccComment & { idx: number }>(r => openEditComment(r.idx)),
    { field: 'text', header: 'Comment', width: 560 },
    { field: 'user', header: 'User', width: 130 },
    { field: 'dt', header: 'DateTime', width: 160, text: r => fmtLocalDateTime(r.dt) },
  ];

  return (
    <AppDialog open width={1150} icon="ti-clipboard-check" className="sr-dlg"
      title={`${header.client || 'Sample'} - Sample Condition Check`} onClose={onClose}
      hotkeys={[
        { combo: 'alt+p', run: () => onReport() },   // SCC Report (Preview)
        { combo: 'alt+n', run: () => openNewComment() },  // open the Comment popup (New)
      ]}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => onSave(record)}>
          <span className="tb-i">💾</span> OK
        </button>
      </>}>
      <button className="ca-btn" style={{ marginBottom: 10 }} onClick={() => onReport()}
        title={`SCC Report (${formatCombo('alt+p')})`}>
        <span className="tb-i tb-i-blue">📄</span> SCC Report
      </button>

      <div className="sr-fgrid" style={{ gridTemplateColumns: '150px minmax(0,1fr) 190px minmax(0,1fr)', marginBottom: 12 }}>
        <label>Job ID</label><input value={jobId || 'Autogenerate'} readOnly />
        <label>Temperature (C)</label>
        {/* Plain text (manual entry) — ALPACA parity: no number spinner / stepper
            (user request 2026-10-06). */}
        <input value={record.temp} onChange={e => set({ temp: e.target.value })} />

        <label>Client</label><input value={header.client} readOnly />
        <label>Corrected Temperature (C)</label>
        <input value={record.corr} onChange={e => set({ corr: e.target.value })} />

        <label>Check In By</label><input value={record.by} readOnly />
        <label>Thermometer ID</label>
        <input value={record.thermo} onChange={e => set({ thermo: e.target.value })} />

        <label>Check In Date</label><input value={fmtLocalDate(record.date)} readOnly />
        <label>Sample pH</label>
        <input value={record.ph} onChange={e => set({ ph: e.target.value })} />

        <label>Sample Matrix</label>
        <select value={record.matrix} onChange={e => set({ matrix: e.target.value })}>
          {(header.matrices.length ? header.matrices : ['Water']).map(m => <option key={m}>{m}</option>)}
        </select>
        <label>pH Paper ID</label>
        <input value={record.paper} onChange={e => set({ paper: e.target.value })} />

        <label>Received Date</label>
        <select value={record.recv} onChange={e => set({ recv: e.target.value })}>
          {receipts.length === 0 && <option value="">N/A</option>}
          {receipts.map(r => <option key={r} value={r}>{fmtLocalDateTime(r)}</option>)}
        </select>
        <span /><span />
      </div>

      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Check Points</div>
      <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-scc">
          <thead>
            <tr>
              <th>CheckPoint</th>
              <th className="sr-scc-ans">Yes</th>
              <th className="sr-scc-ans">No</th>
              <th className="sr-scc-ans">N/A</th>
              <th style={{ minWidth: 320 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {checkPoints.map((p, i) => (
              <tr key={p[0]}>
                <td className="sr-scc-pt">{p[0]}</td>
                {(['yes', 'no', 'na'] as const).map(v => (
                  <td key={v} className="sr-scc-ans">
                    <input type="checkbox" checked={record.points[i] === v}
                      aria-label={`${p[0]} — ${v}`}
                      onChange={() => answer(i, v)} />
                  </td>
                ))}
                <td>
                  <input
                    style={{ width: '100%', border: '1px solid var(--ai-line)', borderRadius: 3, padding: '2px 6px', fontSize: 12 }}
                    value={record.descriptions[i] ?? ''}
                    aria-label={`${p[0]} description`}
                    onChange={e => {
                      const descriptions = [...record.descriptions];
                      descriptions[i] = e.target.value;
                      set({ descriptions });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '12px 0 8px', fontWeight: 800, fontSize: 13 }}>Comment</div>
      {/* New · Edit · Delete live in the grid's own header strip (headerLeft) so they
          stay available when the grid is MAXIMIZED — the old strip above the grid was
          left behind in full-screen mode (Bug 5: no Delete in full screen). */}
      <div className="sr-scc-comments">
        <AppDataGrid<SrSccComment & { idx: number }>
          rows={commentRows} columns={commentCols} getRowId={r => r.idx}
          selectable selectedIds={commentSel} onSelectionChange={setCommentSel}
          defaultPageSize={10} emptyText="No data to display"
          maximizable
          headerLeft={<div className="sr-subtoolbar" style={{ alignItems: 'center' }}>
            <button className="ca-btn" onClick={openNewComment} title={`New comment (${formatCombo('alt+n')})`}>
              <span className="tb-i tb-i-green">＋</span> New
            </button>
            <button className="ca-btn" disabled={commentSel.size !== 1} onClick={editSelectedComment}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
            <button className="ca-btn" disabled={commentSel.size === 0} onClick={() => void deleteSelectedComments()}>
              <span className="tb-i tb-i-red">✕</span> Delete
            </button>
          </div>}
        />
      </div>

      {showCommentDlg && (
        <SccCommentDialog
          initial={editIdx !== null ? (record.comments[editIdx]?.text ?? '') : ''}
          onClose={() => { setShowCommentDlg(false); setEditIdx(null); }}
          onAdd={text => { addComment(text); setShowCommentDlg(false); }}
        />
      )}
    </AppDialog>
  );
}

/** Sample Condition Check Comment popup (ALPACA parity) — a required free-text
 *  comment with OK / Cancel. Enter adds a newline (not submit); OK is disabled
 *  until the comment is non-empty. */
function SccCommentDialog({ initial = '', onClose, onAdd }: { initial?: string; onClose: () => void; onAdd: (text: string) => void }) {
  const [text, setText] = useState(initial);
  const ok = () => { if (text.trim()) onAdd(text); };
  return (
    <AppDialog open width={520} icon="ti-message" submitOnEnter={false}
      title="Sample Condition Check Comment" onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={ok} disabled={!text.trim()}>
          <span className="tb-i">💾</span> OK
        </button>
      </>}>
      <label className="req" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Comment</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={5} autoFocus
        aria-label="Comment"
        placeholder="Note anything wrong or any violation…"
        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)', borderRadius: 4, padding: 8, fontSize: 13, resize: 'vertical' }} />
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom Request — per-test Hold, Sub-out, Default Due Date, Due Date, TAT override
// Matches legacy XAF: Matrix | Test | Method | Hold | Sub Out | DefaultDueDate | DueDate | Parameter | TAT
// ─────────────────────────────────────────────────────────────────────────────
function CustomRequestTab({
  header, templateTests, customRequest, setCustomRequest,
  tatHoursMap, holidaySet, registrationType, onWarn,
}: RegistrationTabsProps) {
  interface CrRow { name: string; method: string; matrix: string }

  // Matrix column shows the job's selected matrix (same for all tests — single
  // matrix per test template picker call). Multiple matrices show semicolon-joined.
  const matrixDisplay = header.matrices.join('; ');

  const rows: CrRow[] = useMemo(
    () => templateTests.map(t => ({ name: t.name, method: t.method, matrix: matrixDisplay })),
    [templateTests, matrixDisplay],
  );

  const tatNames = useMemo(() => Object.keys(tatHoursMap).sort(), [tatHoursMap]);
  const isSampling = registrationType === 'Sampling';

  const entry = (name: string) =>
    customRequest[name] ?? { hold: false, subOut: false, due: '', tat: header.tat };

  const patchCr = (name: string, p: Partial<{ hold: boolean; subOut: boolean; due: string; tat: string }>) =>
    setCustomRequest({ ...customRequest, [name]: { ...entry(name), ...p } });

  // ALPACA parity (SampleRegistrationViewController "DueDate" callback, :13709/:13715):
  // a per-test Due Date may not be before today, nor after the job's default Due Date.
  // A rejected value is cleared back to the default.
  const handleDueChange = (name: string, value: string) => {
    if (value) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (value < todayStr) {
        onWarn('Due date must be greater than or equal to current date.');
        patchCr(name, { due: '' });
        return;
      }
      const defaultDue = toDateInput(header.dueDate);
      if (defaultDue && value > defaultDue) {
        onWarn('Enter the duedate lessthanorequal defaultduedate.');
        patchCr(name, { due: '' });
        return;
      }
    }
    // Bug 11 (ALPACA parity): changing a test's Due Date re-derives its TAT — the
    // LONGEST configured TAT whose due date (Received Date + TAT hours, skipping
    // weekends/holidays) does not fall after the chosen date. Nothing fits → keep it.
    let tat: string | undefined;
    if (value) {
      let best = -1;
      for (const [tatName, hours] of Object.entries(tatHoursMap)) {
        const d = toDateInput(resolveDueDate(header.receivedDate, hours, isSampling, holidaySet));
        if (d && d <= value && hours > best) { best = hours; tat = tatName; }
      }
    }
    patchCr(name, tat ? { due: value, tat } : { due: value });
  };

  const handleTatChange = (name: string, newTat: string) => {
    // Legacy (:14206-14240): a per-test TAT longer than the job's TAT is refused.
    const rowHours = tatHoursMap[newTat];
    const jobHours = tatHoursMap[header.tat];
    if (rowHours !== undefined && jobHours !== undefined && rowHours > jobHours) {
      onWarn('The due date must be less than the default due date.');
      return;
    }
    const hours = tatHoursMap[newTat];
    const newDue = hours !== undefined
      ? toDateInput(resolveDueDate(header.receivedDate, hours, isSampling, holidaySet))
      : entry(name).due;
    patchCr(name, { tat: newTat, due: newDue });
  };

  const cols: AppColumn<CrRow>[] = [
    { field: 'matrix', header: 'Matrix', width: 130 },
    { field: 'name', header: 'Test', width: 220 },
    { field: 'method', header: 'Method', width: 150 },
    {
      field: 'hold', header: 'Hold', width: 60, align: 'center', sortable: false, filterable: false,
      render: r => (
        <input type="checkbox" checked={entry(r.name).hold} aria-label={`Hold ${r.name}`}
          onChange={e => patchCr(r.name, { hold: e.target.checked })} />
      ),
    },
    {
      field: 'subOut', header: 'Sub Out', width: 70, align: 'center', sortable: false, filterable: false,
      render: r => (
        <input type="checkbox" checked={entry(r.name).subOut} aria-label={`Sub Out ${r.name}`}
          onChange={e => patchCr(r.name, { subOut: e.target.checked })} />
      ),
    },
    {
      field: 'defaultDue', header: 'Default Due Date', width: 140,
      text: () => fmtLocalDate(header.dueDate),
    },
    {
      field: 'due', header: 'Due Date', width: 150, sortable: false, filterable: false,
      render: r => (
        <input type="date" value={entry(r.name).due || toDateInput(header.dueDate)}
          aria-label={`Due date for ${r.name}`}
          style={{ border: '1px solid var(--ai-line)', borderRadius: 3, padding: '1px 4px', fontSize: 12 }}
          onChange={e => handleDueChange(r.name, e.target.value)} />
      ),
    },
    {
      field: 'param', header: 'Parameter', width: 100,
      text: () => 'AllParam',
    },
    {
      field: 'tat', header: 'TAT', width: 140, sortable: false, filterable: false,
      render: r => (
        <select
          value={entry(r.name).tat || header.tat}
          aria-label={`TAT for ${r.name}`}
          style={{ border: '1px solid var(--ai-line)', borderRadius: 3, padding: '1px 4px', fontSize: 12, width: '100%', minWidth: 110 }}
          onChange={e => handleTatChange(r.name, e.target.value)}
        >
          {!entry(r.name).tat && !header.tat && <option value="">— Select TAT —</option>}
          {/* Bug 14: the row's own TAT (imported from a Project / pre-log) must always
              be an option, else the select renders blank when it is not in the list. */}
          {[...new Set([entry(r.name).tat || header.tat, ...tatNames].filter(Boolean))]
            .map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      ),
    },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <ExportMenu filename="CustomRequest" {...gridExportMatrix(rows, cols)} />
      </div>
      <AppDataGrid<CrRow>
        rows={rows} columns={cols} getRowId={r => r.name}
        defaultPageSize={20}
        emptyText="Pick a Sample Matrix and tests first."
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notes — author-only edit / delete (§14a Rule C)
// Matches XAF ProjectNotesViewController: two-level popup (list → detail),
// author-only enforcement on edit AND delete, rich text body, Sort field.
// ─────────────────────────────────────────────────────────────────────────────

/** Strip HTML tags → plain text (for grid preview + PlainText column). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Build a blank SrNote for "New". */
function blankNote(user: string, now: string, jobId: string): SrNote {
  return {
    key: nextKey('note'), oid: '', title: '', author: user, authorOid: '',
    date: now, sort: 0, html: '', plainText: '',
    sourceId: jobId, noteSource: 'Sample Registration',
  };
}

function NotesTab({ user, now, tabs, setTabs, jobId, onWarn, confirmDelete }: RegistrationTabsProps) {
  const [editing, setEditing] = useState<SrNote | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  const cols: AppColumn<SrNote>[] = [
    editCol<SrNote>(r => {
      if (notAuthor(r)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
      setEditing({ ...r });
    }),
    { field: 'title', header: 'Title', width: 180 },
    { field: 'plainText', header: 'Note', width: 320, text: r => r.plainText || stripHtml(r.html) },
    { field: 'author', header: 'Author', width: 120 },
    { field: 'date', header: 'Date', width: 150, text: r => fmtLocalDateTime(r.date) },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
  ];

  const save = () => {
    if (!editing) return;
    if (!editing.title.trim()) { onWarn('Enter the Title.'); return; }
    const html = bodyRef.current?.innerHTML ?? editing.html;
    const plain = stripHtml(html);
    if (!plain) { onWarn('Enter the Note text.'); return; }
    const rec: SrNote = { ...editing, html, plainText: plain };
    const exists = tabs.notes.some(n => n.key === rec.key);
    setTabs({ ...tabs, notes: exists ? tabs.notes.map(n => (n.key === rec.key ? rec : n)) : [...tabs.notes, rec] });
    setEditing(null);
  };

  /** §14a Rule C — a note may be edited or deleted ONLY by its author. */
  const notAuthor = (n: SrNote) => n.author !== user;

  const tryEdit = () => {
    const n = tabs.notes.find(x => sel.has(x.key));
    if (!n) return;
    if (notAuthor(n)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
    setEditing({ ...n });
  };

  const tryDelete = () => {
    const picked = tabs.notes.filter(n => sel.has(n.key));
    if (picked.some(notAuthor)) {
      onWarn('You are not authorized to modify a note created by another author.');
      return;
    }
    confirmDelete(`${sel.size} note(s)`, () => {
      setTabs({ ...tabs, notes: tabs.notes.filter(n => !sel.has(n.key)) });
      setSel(new Set());
    });
  };

  return (
    <>
      <AppToolbar
        onNew={() => setEditing(blankNote(user, now, jobId))}
        onEdit={tryEdit}
        editDisabled={sel.size !== 1}
        onDelete={tryDelete}
        deleteDisabled={sel.size === 0}
        exportData={{ filename: 'Notes', ...gridExportMatrix(tabs.notes, cols) }}
      />
      <AppDataGrid<SrNote>
        rows={tabs.notes} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {editing && (
        <AppDialog open title="Note"
          icon="ti-note" width={750} className="sr-dlg" onClose={() => setEditing(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save}>OK</button>
          </>}>
          <div className="sr-note-grid">
            <label className="req">Title:</label>
            <div className="sr-note-val">
              {!editing.title.trim() && <span className="sr-val-icon" title="Required">⊘</span>}
              <input value={editing.title} className={editing.title.trim() ? undefined : 'sr-req-empty'}
                onChange={e => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <label>Business Object:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />

            <label>Source ID:</label>
            <input value={editing.sourceId || jobId} readOnly />
            <label>Author:</label>
            <input value={editing.author} readOnly />

            <label>Name Source:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />
            <label>Date:</label>
            <input value={fmtLocalDateTime(editing.date)} readOnly />
          </div>
          <div className="sr-rte-wrap">
            <span className="sr-rte-tab">Note</span>
            <div className="sr-rte">
              <div className="sr-rte-bar">
                <button className="sr-rte-btn" onClick={() => document.execCommand('undo')} title="Undo"><i className="ti ti-arrow-back-up" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('redo')} title="Redo"><i className="ti ti-arrow-forward-up" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-btn" onClick={() => document.execCommand('copy')} title="Copy"><i className="ti ti-copy" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('cut')} title="Cut"><i className="ti ti-cut" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('paste')} title="Paste"><i className="ti ti-clipboard" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-dd sr-rte-font" onClick={() => {}}>Calibri</button>
                <button className="sr-rte-dd" onClick={() => document.execCommand('bold')}>Font</button>
                <button className="sr-rte-dd" onClick={() => {}}>Paragraph</button>
                <button className="sr-rte-dd" onClick={() => {}}>Styles</button>
                <button className="sr-rte-dd" onClick={() => {}}>Editing</button>
              </div>
              <div ref={bodyRef} className="sr-rte-body" contentEditable suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: editing.html }} />
            </div>
          </div>
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Item Charge — extra line items that roll into the invoice
// ─────────────────────────────────────────────────────────────────────────────
function ItemChargeTab({ tabs, setTabs, confirmDelete, onWarn }: RegistrationTabsProps) {
  const [pricing, setPricing] = useState<IcPricingOption[]>([]);
  // Legacy parity: Add opens an "Item Charge" picker (search + ItemCode / ItemName / Unit /
  // UnitPrice grid, OK / Cancel) over the price list — Add itself is always enabled.
  const [picking, setPicking] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickSel, setPickSel] = useState<Set<string | number>>(new Set());
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    api.get('/api/v1/sample-registration/item-charge-pricing')
      .then(res => {
        if (!cancelled && res.data?.data) {
          const items: IcPricingOption[] = res.data.data;
          setPricing(items);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const money = (n: number) => `$${n.toFixed(2)}`;

  const openAdd = () => { setPickSearch(''); setPickSel(new Set()); setPicking(true); };

  /** OK in the picker: one row (qty 1, no discount) per ticked price-list item. */
  const addPicked = () => {
    const chosen = pricing.filter(p => pickSel.has(p.oid));
    if (chosen.length === 0) { onWarn('Select at least one item.'); return; }
    const rows: SrItemCharge[] = chosen.map(it => ({
      key: nextKey('ic'), oid: '', itemPriceOid: it.oid, name: it.itemName, desc: '',
      price: it.unitPrice, qty: 1, amount: it.unitPrice, final: it.unitPrice,
      npPrice: it.npUnitPrice, disc: 0, remark: '',
    }));
    setTabs({ ...tabs, itemCharges: [...tabs.itemCharges, ...rows] });
    setPicking(false);
    setSel(new Set());
  };

  const pickRows = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return q ? pricing.filter(p => `${p.itemCode} ${p.itemName} ${p.unit ?? ''}`.toLowerCase().includes(q)) : pricing;
  }, [pricing, pickSearch]);
  const pickCols: AppColumn<IcPricingOption>[] = [
    { field: 'itemCode', header: 'ItemCode', width: 120 },
    { field: 'itemName', header: 'ItemName', width: 220 },
    { field: 'unit', header: 'Unit', width: 100 },
    { field: 'unitPrice', header: 'UnitPrice', type: 'number', width: 100, text: r => money(r.unitPrice) },
  ];

  const cols: AppColumn<SrItemCharge>[] = [
    { field: 'name', header: 'ItemName', width: 180 },
    { field: 'desc', header: 'Description', width: 200 },
    { field: 'qty', header: 'Qty', type: 'number', width: 70 },
    { field: 'price', header: 'UnitPrice', type: 'number', width: 100, text: r => money(r.price) },
    { field: 'amount', header: 'Amount', type: 'number', width: 100, text: r => money(r.amount) },
    { field: 'disc', header: 'Discount(%)', type: 'number', width: 110 },
    { field: 'final', header: 'FinalAmount', type: 'number', width: 120, text: r => money(r.final) },
    { field: 'remark', header: 'Remark', width: 150 },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <button className="ca-btn" onClick={openAdd}>
          <span className="tb-i tb-i-green">＋</span> Add
        </button>
        <button className="ca-btn"
          onClick={() => {
            if (sel.size === 0) { onWarn('Select at least one item charge to remove.'); return; }
            confirmDelete(`${sel.size} item charge(s)`, () => {
              setTabs({ ...tabs, itemCharges: tabs.itemCharges.filter(c => !sel.has(c.key)) });
              setSel(new Set());
            });
          }}>
          <span className="tb-i tb-i-red">－</span> Remove
        </button>
        <ExportMenu filename="ItemCharge" {...gridExportMatrix(tabs.itemCharges, cols)} />
      </div>
      <AppDataGrid<SrItemCharge>
        rows={tabs.itemCharges} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {picking && (
        <AppDialog open title="Item Charge" icon="ti-receipt" width={760} className="sr-dlg"
          onClose={() => setPicking(false)}
          footer={<>
            <button className="ca-btn ca-btn-solid" onClick={addPicked}>OK</button>
            <button className="ca-btn" onClick={() => setPicking(false)}>Cancel</button>
          </>}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <input value={pickSearch} onChange={e => setPickSearch(e.target.value)}
              placeholder="Search…" aria-label="Search items" style={{ width: 240 }} />
          </div>
          <AppDataGrid<IcPricingOption>
            rows={pickRows} columns={pickCols} getRowId={r => r.oid}
            selectable selectedIds={pickSel} onSelectionChange={setPickSel}
            defaultPageSize={10} emptyText="No data to display"
          />
        </AppDialog>
      )}

    </>
  );
}

/** Small in-grid picture for an Image Edit row (same authenticated download as the preview). */
function ImageThumb({ image }: { image: SrImage }) {
  const imageOid = image.oid || image.fileDataOid;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!imageOid) { setUrl(null); return; }
    let objectUrl: string | null = null;
    const ac = new AbortController();
    void api.get(`/api/v1/sample-registration/images/${encodeURIComponent(imageOid)}/download`,
      { responseType: 'blob', signal: ac.signal })
      .then(res => { objectUrl = URL.createObjectURL(res.data as Blob); setUrl(objectUrl); })
      .catch(() => { if (!ac.signal.aborted) setUrl(null); });
    return () => { ac.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [imageOid]);
  return url
    ? <img src={url} alt={image.name} style={{ height: 44, maxWidth: 120, objectFit: 'contain', display: 'block' }} />
    : <span>🖼 {image.photo || 'Image attached'}</span>;
}

/** Preview body for one Image Edit row: fetches the stored binary
 *  (GET images/{oid}/download — through `api`, so the JWT rides along) and shows it.
 *  The row's `oid` is the dbo.Image Oid for both a saved row and a just-uploaded one. */
function ImagePreviewBody({ image }: { image: SrImage }) {
  const imageOid = image.oid || image.fileDataOid;
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'none'>(imageOid ? 'loading' : 'none');

  useEffect(() => {
    if (!imageOid) { setState('none'); return; }
    let objectUrl: string | null = null;
    const ac = new AbortController();
    setState('loading');
    void api.get(`/api/v1/sample-registration/images/${encodeURIComponent(imageOid)}/download`,
      { responseType: 'blob', signal: ac.signal })
      .then(res => {
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
        setState('ok');
      })
      .catch(() => { if (!ac.signal.aborted) setState('none'); });
    return () => { ac.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [imageOid]);

  return (
    <div style={{ textAlign: 'center', padding: 20, background: 'var(--ai-readonly-bg)', border: '1px dashed #b9c4d0', borderRadius: 6 }}>
      {state === 'ok' && url ? (
        <img src={url} alt={image.name} style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }} />
      ) : (
        <>
          <div style={{ fontSize: 52 }}>🖼</div>
          <div style={{ fontWeight: 700, marginTop: 8 }}>
            {state === 'loading' ? 'Loading…' : '(no photo attached)'}
          </div>
        </>
      )}
      {image.photo && <div style={{ marginTop: 8, fontWeight: 600 }}>{image.photo}</div>}
      <div className="sr-hint-inline">{image.comment}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Image Edit
// ─────────────────────────────────────────────────────────────────────────────
function ImageEditTab({ tabs, setTabs, onWarn, onSuccess, confirmDelete }: RegistrationTabsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SrImage>({ key: '', oid: '', name: '', photo: '', fileDataOid: '', sort: 0, comment: '' });
  const [uploading, setUploading] = useState(false);
  const imgInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<SrImage | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  // Bug 10 (G5 parity): per-row Edit + Preview icons, an Edit toolbar button, and
  // the picture itself (thumbnail) in SamplePhoto instead of the text "Image attached".
  const openEdit = (m: SrImage) => { setDraft({ ...m }); setOpen(true); };
  const cols: AppColumn<SrImage>[] = [
    {
      field: '__act', header: '', width: 62, align: 'center', sortable: false, filterable: false, noExport: true,
      render: r => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <button className="sr-edit-cell" title="Edit" onClick={e => { e.stopPropagation(); openEdit(r); }}>✎</button>
          <button className="sr-edit-cell" title="Preview" onClick={e => { e.stopPropagation(); setPreview(r); }}>👁</button>
        </span>
      ),
    },
    { field: 'name', header: 'Name', width: 180 },
    {
      field: 'photo', header: 'SamplePhoto', width: 220,
      text: r => r.photo || (r.oid || r.fileDataOid ? 'Image attached' : '—'),
      render: r => (r.oid || r.fileDataOid) ? <ImageThumb image={r} /> : <span>—</span>,
    },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
    { field: 'comment', header: 'Comment', width: 260 },
  ];

  const add = () => {
    if (!draft.name.trim()) { onWarn('Enter the Name.'); return; }
    const saved = { ...draft, oid: draft.fileDataOid || draft.oid || '' };
    setTabs({
      ...tabs,
      images: draft.key && tabs.images.some(i => i.key === draft.key)
        ? tabs.images.map(i => (i.key === draft.key ? saved : i))        // Edit
        : [...tabs.images, { ...saved, key: nextKey('img') }],            // New
    });
    setOpen(false);
  };

  return (
    <>
      <div className="sr-subtoolbar">
        <button className="ca-btn"
          onClick={() => { setDraft({ key: '', oid: '', name: '', photo: '', fileDataOid: '', sort: 0, comment: '' }); setOpen(true); }}>
          <span className="tb-i tb-i-green">＋</span> New
        </button>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmDelete(`${sel.size} image(s)`, () => {
            setTabs({ ...tabs, images: tabs.images.filter(m => !sel.has(m.key)) });
            setSel(new Set());
          })}>
          <span className="tb-i tb-i-red">🔗</span> Unlink
        </button>
        <button className="ca-btn" disabled={sel.size !== 1}
          onClick={() => { const m = tabs.images.find(x => sel.has(x.key)); if (m) openEdit(m); }}>
          <span className="tb-i tb-i-blue">✎</span> Edit
        </button>
        <button className="ca-btn" disabled={sel.size !== 1}
          onClick={() => {
            const m = tabs.images.find(x => sel.has(x.key));
            if (m) setPreview(m);
          }}>
          <span className="tb-i tb-i-blue">👁</span> Preview
        </button>
        <ExportMenu filename="ImageEdit" {...gridExportMatrix(tabs.images, cols)} />
      </div>
      <AppDataGrid<SrImage>
        rows={tabs.images} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={10} emptyText="No data to display"
      />

      {open && (
        <AppDialog open title="Image" icon="ti-photo" width={520} className="sr-dlg" onClose={() => setOpen(false)}
          footer={<>
            <button className="ca-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={add}><span className="tb-i">💾</span> OK</button>
          </>}>
          <div className="sr-fgrid" style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
            <label className="req">Name</label>
            <input value={draft.name} className={draft.name ? undefined : 'sr-req-empty'}
              onChange={e => setDraft({ ...draft, name: e.target.value })} />
            <label>SamplePhoto</label>
            {/* Legacy ImageUpload is an XAF ImageEditor: it SHOWS the current picture and a new
                browse replaces it. A bare native <input type="file"> always reads "No file
                chosen" on Edit even when an image is attached, so show the stored image + name
                and drive a hidden input from a Change button (owner report 2026-10-15). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {(draft.oid || draft.fileDataOid) && <ImageThumb image={draft} />}
              <button type="button" className="ca-btn" onClick={() => imgInputRef.current?.click()}>
                {(draft.oid || draft.fileDataOid) ? 'Change file' : 'Choose file'}
              </button>
              <span style={{ fontSize: 12, color: 'var(--ai-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {draft.photo || ((draft.oid || draft.fileDataOid) ? 'Image attached' : 'No file chosen')}
              </span>
            </div>
            <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={async e => {
                const file = e.target.files?.[0];
                e.target.value = '';                  // allow re-picking the same file
                if (!file) return;
                setDraft(d => ({ ...d, photo: file.name }));
                setUploading(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  const res = await api.post('/api/v1/sample-registration/images/upload', fd);
                  const imageOid = (res.data as { data?: { imageOid?: string } }).data?.imageOid ?? '';
                  setDraft(d => ({ ...d, fileDataOid: imageOid }));
                  onSuccess('Image uploaded.');
                } catch {
                  onWarn('Image upload failed.');
                  setDraft(d => ({ ...d, photo: '', fileDataOid: '' }));
                } finally { setUploading(false); }
              }} />
            {uploading && <><label /><span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>Uploading...</span></>}
            <label>Sort</label>
            <span className="sr-numspin">
              <button type="button" onClick={() => setDraft(d => ({ ...d, sort: Math.max(0, d.sort - 1) }))}>−</button>
              <input value={draft.sort} readOnly aria-label="Sort" />
              <button type="button" onClick={() => setDraft(d => ({ ...d, sort: d.sort + 1 }))}>＋</button>
            </span>
            <label>Comment</label>
            <input value={draft.comment} onChange={e => setDraft({ ...draft, comment: e.target.value })} />
          </div>
        </AppDialog>
      )}

      {preview && (
        <AppDialog open title={`Preview — ${preview.name}`} icon="ti-photo" width={520} className="sr-dlg"
          onClose={() => setPreview(null)}
          footer={<button className="ca-btn" onClick={() => setPreview(null)}>Close</button>}>
          <ImagePreviewBody image={preview} />
        </AppDialog>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-Invoice — read-only: InvoicingAnalysisCharge + InvoicingItemCharge
// XAF parity: PreInvoicing_DetailView
// ─────────────────────────────────────────────────────────────────────────────
interface PreInvoiceAnalysisCharge {
  oid: string;
  matrixName: string;
  testName: string;
  methodName: string;
  componentName: string;
  parameter: string;
  chargeType: number;
  priceCode: string;
  qty: number;
  tierNo: number;
  tierPrice: number;
  prep1Price: number;
  prep2Price: number;
  totalUnitPrice: number;
  discount: number;
  amount: number;
}
interface PreInvoiceItemCharge {
  oid: string;
  itemName: string;
  description: string;
  qty: number;
  unitPrice: number;
  discount: number;
  amount: number;
  finalAmount: number;
  remark: string;
}

function PreInvoiceTab({ jobId, jobOid, onWarn, onSuccess }: RegistrationTabsProps) {
  const [analysisRows, setAnalysisRows] = useState<PreInvoiceAnalysisCharge[]>([]);
  const [itemRows, setItemRows]         = useState<PreInvoiceItemCharge[]>([]);
  const [loading, setLoading]           = useState(false);
  const [recalcBusy, setRecalcBusy]     = useState(false);

  // Load pre-invoice data when the tab mounts (requires a saved job)
  useEffect(() => {
    if (!jobOid) return;
    setLoading(true);
    api.get<{ success: boolean; data?: { analysisCharges: PreInvoiceAnalysisCharge[]; itemCharges: PreInvoiceItemCharge[] } }>(
      `/api/v1/sample-registration/${jobOid}/pre-invoice`,
    )
      .then(res => {
        if (res.data.success && res.data.data) {
          setAnalysisRows(res.data.data.analysisCharges ?? []);
          setItemRows(res.data.data.itemCharges ?? []);
        }
      })
      .catch(() => onWarn('Failed to load pre-invoice data.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOid]);

  const handleRecalculate = async () => {
    if (!jobOid || !jobId) { onWarn('Save the job first before recalculating the invoice.'); return; }
    setRecalcBusy(true);
    try {
      // jobId is the string JobID (e.g. "IH-2025-0001"), but the recalculate
      // endpoint on the existing controller takes a string jobId route param.
      // We need to get it from the analysisRows or pass jobOid and let the
      // server resolve — the existing endpoint is POST /{jobId}/recalculate-invoice
      // where jobId is the string JobID. Refresh the grid after.
      await api.post(`/api/v1/sample-registration/${jobId}/recalculate-invoice`);
      onSuccess('Invoice recalculated successfully.');
      // Reload
      const res = await api.get<{ success: boolean; data?: { analysisCharges: PreInvoiceAnalysisCharge[]; itemCharges: PreInvoiceItemCharge[] } }>(
        `/api/v1/sample-registration/${jobOid}/pre-invoice`,
      );
      if (res.data.success && res.data.data) {
        setAnalysisRows(res.data.data.analysisCharges ?? []);
        setItemRows(res.data.data.itemCharges ?? []);
      }
    } catch {
      onWarn('Failed to recalculate invoice.');
    } finally {
      setRecalcBusy(false);
    }
  };

  const fmt2 = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const analysisCols: AppColumn<PreInvoiceAnalysisCharge>[] = [
    { field: 'matrixName',     header: 'Matrix',          width: 110, filterable: true  },
    { field: 'testName',       header: 'Test',            width: 160, filterable: true  },
    { field: 'methodName',     header: 'Method',          width: 120, filterable: true  },
    { field: 'componentName',  header: 'Component',       width: 130, filterable: true  },
    { field: 'parameter',      header: 'Parameter',       width: 120, filterable: true  },
    { field: 'priceCode',      header: 'Price Code',      width: 100, filterable: true  },
    { field: 'qty',            header: 'Qty',             width:  70, align: 'right', render: r => <>{fmt2(r.qty)}</> },
    { field: 'tierNo',         header: 'Tier',            width:  60, align: 'right', render: r => <>{fmt2(r.tierNo)}</> },
    { field: 'tierPrice',      header: 'Tier Price',      width:  90, align: 'right', render: r => <>{fmt2(r.tierPrice)}</> },
    { field: 'prep1Price',     header: 'Prep 1',          width:  80, align: 'right', render: r => <>{fmt2(r.prep1Price)}</> },
    { field: 'prep2Price',     header: 'Prep 2',          width:  80, align: 'right', render: r => <>{fmt2(r.prep2Price)}</> },
    { field: 'totalUnitPrice', header: 'Total Unit Price', width: 115, align: 'right', render: r => <>{fmt2(r.totalUnitPrice)}</> },
    { field: 'discount',       header: 'Discount',        width:  85, align: 'right', render: r => <>{fmt2(r.discount)}</> },
    { field: 'amount',         header: 'Amount',          width:  90, align: 'right', render: r => <>{fmt2(r.amount)}</> },
  ];

  const itemCols: AppColumn<PreInvoiceItemCharge>[] = [
    { field: 'itemName',    header: 'Item',        width: 160, filterable: true },
    { field: 'description', header: 'Description', width: 200, filterable: true },
    { field: 'qty',         header: 'Qty',         width:  70, align: 'right', render: r => <>{fmt2(r.qty)}</> },
    { field: 'unitPrice',   header: 'Unit Price',  width:  95, align: 'right', render: r => <>{fmt2(r.unitPrice)}</> },
    { field: 'discount',    header: 'Discount',    width:  85, align: 'right', render: r => <>{fmt2(r.discount)}</> },
    { field: 'amount',      header: 'Amount',      width:  90, align: 'right', render: r => <>{fmt2(r.amount)}</> },
    { field: 'finalAmount', header: 'Final Amt',   width:  90, align: 'right', render: r => <>{fmt2(r.finalAmount)}</> },
    { field: 'remark',      header: 'Remark',      width: 180, filterable: true },
  ];

  // Shared hotkey (standing rule 14, §18.6, ADR-0013): the bespoke Recalculate
  // Invoice button.  Called UNCONDITIONALLY above the `!jobOid` early return
  // (rules of hooks); the def is gated to match the button, which renders only
  // when jobOid is set (the early return replaces it otherwise).
  useHotkeys(jobOid ? [
    { combo: 'alt+c', label: 'Recalculate Invoice', group: 'Sample Registration',
      run: () => void handleRecalculate(), disabled: recalcBusy },
  ] : []);

  if (!jobOid) {
    return (
      <div className="sr-hint-inline" style={{ padding: '12px 0' }}>
        Save the job to view pre-invoice data.
      </div>
    );
  }

  return (
    <>
      <AppToolbar
        left={
          <button
            className="ca-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            disabled={recalcBusy}
            title={`Recalculate Invoice (${formatCombo('alt+c')})`}
            onClick={handleRecalculate}
          >
            <i className={`ti ${recalcBusy ? 'ti-loader-2 sr-spin' : 'ti-refresh'}`} />
            Recalculate Invoice
          </button>
        }
        exportData={{ filename: 'PreInvoice-AnalysisCharges', ...gridExportMatrix(analysisRows, analysisCols) }}
      />

      <div style={{ fontWeight: 600, fontSize: 13, margin: '10px 0 4px', color: 'var(--ai-ink)' }}>
        Analysis Charges
      </div>
      <AppDataGrid<PreInvoiceAnalysisCharge>
        rows={analysisRows}
        columns={analysisCols}
        getRowId={(r) => r.oid}
        loading={loading}
        defaultPageSize={20}
      />

      <div style={{ fontWeight: 600, fontSize: 13, margin: '14px 0 4px', color: 'var(--ai-ink)' }}>
        Item Charges
      </div>
      <AppDataGrid<PreInvoiceItemCharge>
        rows={itemRows}
        columns={itemCols}
        getRowId={(r) => r.oid}
        loading={loading}
        defaultPageSize={20}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared column helper
// ─────────────────────────────────────────────────────────────────────────────
/** The narrow row-edit pencil column every tab grid starts with. */
function editCol<T>(onEdit: (row: T) => void): AppColumn<T> {
  return {
    field: '__edit', header: '', width: 34, align: 'center',
    sortable: false, filterable: false, noExport: true,
    render: row => (
      <button className="sr-edit-cell" title="Edit"
        onClick={e => { e.stopPropagation(); onEdit(row); }}>✎</button>
    ),
  };
}
